import { minutesToMilliseconds } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/button';
import { usePauseTimeTracking } from '../../hooks/timeTracking';
import { useClockOutGuard } from '../../hooks/timeTracking/useClockOutGuard';
import { Working } from '../../hooks/useCurrentTimeTrackingTimeline';
import { ClockoutModals } from '../../pages/TimeTracking/CurrentTimeTracking/ClockoutModals';
import TimeUntilNow from '../../pages/TimeTracking/CurrentTimeTracking/TimeUntilNow';
import { BreakTypeInfo } from '../BreakTypeIcon';
import BreakTypeButtons from '../BreakTypeButtons';
import { roundSmCircleIconButtonStyle } from '../roundSmCircleIconButtonStyle';
import AccordionToggleButton from './AccordionToggleButton';
import CardTitle from './CardTitle';
import { ExtraFieldInternalValue } from '../ExtraFields/extraFieldInternalValue';
import { ExtraFieldContextParam } from '../ExtraFields/types';

type Props = {
  step: Working;
  notes: string | null;
  lastModifiedDate: string;
  accordionToggleKey: string;
  accordionOpen: boolean;
  accordionToggleOpen: () => void;
  breakTypes?: BreakTypeInfo[];
  extraFieldValues: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
  contextParams: ExtraFieldContextParam[];
};

const CardHeaderWorkingStep: React.FC<React.PropsWithChildren<Props>> = ({
  step,
  notes,
  lastModifiedDate,
  accordionToggleKey,
  accordionOpen,
  accordionToggleOpen,
  breakTypes,
  extraFieldValues,
  recordTypeName,
  contextParams,
}) => {
  const { timeEntryId } = step;
  const [showModalClockoutDate, setShowModalClockoutDate] =
    useState<Date | null>(null);
  const { t } = useTranslation();
  const { pauseTimeTracking, loading: pausing } =
    usePauseTimeTracking(timeEntryId);

  const guardClockOut = useClockOutGuard();
  const handleCloseModal = useCallback(
    () => setShowModalClockoutDate(null),
    [setShowModalClockoutDate],
  );
  const handleShowModal = useCallback(() => {
    if (guardClockOut()) {
      setShowModalClockoutDate(new Date());
    }
  }, [guardClockOut, setShowModalClockoutDate]);
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <CardTitle
            title={
              <TimeUntilNow
                date={step.startTime}
                offset={minutesToMilliseconds(step.totalBreakTime)}
              />
            }
            subtitle={t('timeTracking.current.steps.working.workingTime')}
            accordionToggleKey={accordionToggleKey}
            accordionToggleOpen={accordionToggleOpen}
          />
        </Col>

        <Col className="col-auto">
          <BreakTypeButtons
            breakTypes={breakTypes}
            onPause={pauseTimeTracking}
            loading={pausing}
            rounded
          />
          <Button
            size="sm"
            variant="outline-danger"
            className="lift btn-rounded-circle me-2 d-inline-flex align-items-center justify-content-center"
            style={roundSmCircleIconButtonStyle}
            onClick={handleShowModal}
            icon="stop-outline"
            disabled={pausing}
            tooltip={t('timeTracking.current.steps.working.clockOut.tooltip')}
          />

          <AccordionToggleButton
            eventKey={accordionToggleKey}
            open={accordionOpen}
            onClick={accordionToggleOpen}
          />
        </Col>
      </Row>
      <ClockoutModals
        key={`stop-tracking-modal-${lastModifiedDate}`}
        notes={notes}
        clockOutDate={showModalClockoutDate}
        onClose={handleCloseModal}
        extraFieldValues={extraFieldValues}
        recordTypeName={recordTypeName}
        contextParams={contextParams}
      />
    </>
  );
};

export default CardHeaderWorkingStep;
